import {
  customizeError,
  getResponseData,
  checkEnterpriseId
} from "@helpers/_functions";

import http from "../http-common";
import BaseService from "./base.service";
import {
  createActiveTimeclockConfirmationCancelledError,
  isActiveTimeclockWorkingHoursError,
  requestActiveTimeclockConfirmation
} from "./activeTimeclockConfirmation.service";

class WorkingHourService extends BaseService {
  constructor() {
    super("working-hours");
  }

  addForceActiveTimeclockCleanup(data) {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      data.set("forceActiveTimeclockCleanup", "true");
      return data;
    }

    return {
      ...data,
      forceActiveTimeclockCleanup: true
    };
  }

  async withActiveTimeclockConfirmation(requestFactory, data) {
    try {
      return await requestFactory(data);
    } catch (err) {
      if (!isActiveTimeclockWorkingHoursError(err)) {
        throw err;
      }

      const shouldContinue = await requestActiveTimeclockConfirmation(err);
      if (!shouldContinue) {
        throw createActiveTimeclockConfirmationCancelledError(err);
      }

      return requestFactory(this.addForceActiveTimeclockCleanup(data));
    }
  }

  insert(data, params = {}) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/create`, payload, { params }),
      data
    );
  }

  getByEmployer(id) {
    return http.get(`${this.path}/getByEmployer/${id}`);
  }
  getByEmployeeAndDate(id, selectedDate = "") {
    return http.get(`${this.path}/getByEmployeeAndDate/${id}/${selectedDate}`);
  }
  getByEmployeeAndPeriod(id, startDate, endDate) {
    return http.get(`${this.path}/getByEmployeeAndPeriod/${id}/${startDate}/${endDate}`);
  }
  hasEndingAfterStartDate(workingHour) {
    return http
      .post(`${this.path}/has-ending-after-start-date`, {
        _id: workingHour?._id,
        Employer: workingHour?.Employer,
        startDate: workingHour?.startDate,
        currentDate: new Date()
      })
      .then((response) => response.data?.data);
  }
  downloadGabarit() {
    return http.get(`${this.path}/gabarit`, {
      responseType: "blob"
    });
  }
  importSchedule(data) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/import`, payload),
      data
    )
      .then((response) => response.data)
      .catch((err) => Promise.reject(customizeError(err)));
  }
  injectPlannedHours(data) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/inject-planned-hours`, payload),
      data
    )
      .then((response) => response.data)
      .catch((err) => Promise.reject(customizeError(err)));
  }
  roundingHours(data) {
    return http
      .post(`${this.path}/rounding-hours`, data)
      .then((response) => response.data)
      .catch((err) => Promise.reject(customizeError(err)));
  }
  createBlocksHours(blocksHours) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/create_blocks`, payload),
      blocksHours
    );
  }
  upsertManyHours(hours) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/upsert_hours`, payload),
      hours
    );
  }
  getImagesByEmployer(id, params) {
    return http.get(`${this.path}/getImagesByEmployer/${id}`, { params });
  }

  verifyDuplicatedWorkingHours(data) {
    return http.post(`${this.path}/verify-duplicated-working-hours`, data);
  }
  verifyLeaves(data) {
    return http.post(`${this.path}/validate-with-leaves`, data);
  }
  getLastShiftByEmployer(id) {
    return http.get(`${this.path}/getLastShiftByEmployer/${id}`);
  }
  getLastEndDateByEmploye(employeId, timezone, data) {
    const encodedTimezone = encodeURIComponent(timezone);
    const url = `${this.path}/getLastEndDateByEmploye/${employeId}/${encodedTimezone}`;
    const encodedUrl = encodeURI(url);
    return http.post(encodedUrl, data);
  }
  getDetailedReport(userId, enterpriseId, filter) {
    return http.post(
      `${this.path}/getDetailedReport/${userId}/${enterpriseId}`,
      filter
    );
  }
  getDuplicatesByPeriod(params) {
    return http
      .get(`${this.path}/getDuplicatesByPeriod/${params?.userId}`, {
        params
      })
      .then((response) => getResponseData(response));
  }

  insertMany(data) {
    return this.withActiveTimeclockConfirmation(
      (payload) => http.post(`${this.path}/create-many`, payload),
      data
    );
  }

  getBankHourLogs(employeId, language) {
    const enterpriseId = checkEnterpriseId();
    return http
      .get(
        `${this.path}/hour-bank-logs/${enterpriseId}/${employeId}/${language}`
      )
      .then((response) => getResponseData(response))
      .catch((err) => Promise.reject(customizeError(err)));
  }

  deleteByPeriod(body) {
    return http.post(`${this.path}/delete-by-period`, body);
  }

  updateHoursType(body) {
    return http.post(`${this.path}/update-hourstype`, body);
  }

  simpleUpdateById(id, body) {
    return http.put(`${this.path}/simple-update/${id}`, body);
  }
}

export default new WorkingHourService();
